<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div class="chart-card__header-left">
        <h3 class="chart-card__title">{{ title }}</h3>
        <span v-if="subtitle" class="chart-card__subtitle">{{ subtitle }}</span>
      </div>
      <span v-if="totalSales" class="chart-card__total">
        Totalt: {{ formattedTotal }}
      </span>
    </div>
    <div class="chart-card__body">
      <apexchart
        v-if="hasData"
        type="bar"
        :height="chartHeight"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="chart-card__empty">
        <font-awesome-icon icon="store" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'LocationComparisonChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    title: {
      type: String,
      default: 'Salg per avdeling'
    },
    subtitle: {
      type: String,
      default: ''
    },
    locations: {
      type: Array,
      default: () => []
      // Expected format: [{ name: 'Location 1', sales: 45000, isClosed: false }, ...]
    },
    height: {
      type: Number,
      default: 250
    }
  },
  computed: {
    hasData() {
      return this.locations && this.locations.length > 0;
    },
    sortedLocations() {
      return [...this.locations].sort((a, b) => b.sales - a.sales);
    },
    totalSales() {
      return this.locations.reduce((sum, loc) => sum + (loc.sales || 0), 0);
    },
    formattedTotal() {
      return new Intl.NumberFormat('nb-NO').format(this.totalSales) + ' kr';
    },
    chartHeight() {
      // Dynamic height based on number of locations (min 150, max 400)
      const baseHeight = Math.max(150, Math.min(400, this.locations.length * 50 + 50));
      return baseHeight;
    },
    chartSeries() {
      return [{
        name: 'Salg',
        data: this.sortedLocations.map(loc => loc.sales || 0)
      }];
    },
    chartOptions() {
      const maxSales = Math.max(...this.sortedLocations.map(loc => loc.sales || 0));
      const self = this;

      return {
        chart: {
          type: 'bar',
          fontFamily: 'inherit',
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 500
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: '70%',
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        colors: this.sortedLocations.map((loc, index) => {
          if (loc.isClosed) return 'var(--bo-line)';
          if (index === 0) return '#B30E10'; // Top performer
          if (loc.sales === maxSales) return '#B30E10';
          return '#FFB5B5';
        }),
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
          },
          offsetX: 10,
          style: {
            fontSize: '12px',
            fontWeight: 600,
            colors: ['var(--bo-ink)']
          }
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.sortedLocations.map(loc => loc.name),
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '12px'
            },
            formatter: (val) => {
              if (val >= 1000) {
                return (val / 1000).toFixed(0) + 'k';
              }
              return val;
            }
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: 'var(--bo-ink)',
              fontSize: '13px',
              fontWeight: 500
            },
            formatter: function(val, index) {
              const loc = self.sortedLocations[index];
              if (loc && loc.isClosed) {
                return val + ' (Stengt)';
              }
              return val;
            }
          }
        },
        grid: {
          borderColor: 'var(--bo-surface-2)',
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: true
            }
          },
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => {
              return new Intl.NumberFormat('nb-NO').format(val) + ' kr';
            }
          }
        }
      };
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.chart-card__header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0;
}

.chart-card__subtitle {
  font-size: 13px;
  color: var(--bo-muted);
}

.chart-card__total {
  font-size: 14px;
  font-weight: 600;
  color: var(--bo-alert-ink);
  background: var(--bo-bad-bg);
  padding: 6px 12px;
  border-radius: 6px;
}

.chart-card__body {
  min-height: 150px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 150px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.5;
}
</style>
